import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useConsent } from "../lib/consent/store";
import { logBannerShown } from "../lib/consent/measure";
import ConsentPreferences from "./ConsentPreferences";

export default function ConsentBanner() {
  const { isDecided, isHydrated, acceptAll, rejectAll, openPreferences } = useConsent();

  useEffect(() => {
    // Wait for hydration: before it, isDecided is false even for returning
    // visitors who already decided, which would inflate the denominator.
    if (isHydrated && !isDecided) logBannerShown();
  }, [isHydrated, isDecided]);

  return (
    <>
      <AnimatePresence>
        {!isDecided && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
            className="fixed inset-x-0 bottom-0 z-[90] p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
          >
            <div className="max-w-5xl mx-auto bg-paper border border-ink/15 rounded-2xl shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-5">
                <div className="flex-1">
                  <h2 className="text-base md:text-lg font-serif text-ink">
                    We use cookies to make LetsLoop better.
                  </h2>
                  <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">
                    Essential cookies keep the site running. With your
                    permission, we&rsquo;d also use analytics cookies to learn
                    what&rsquo;s working, and marketing cookies to reach the
                    right people. You can change this any time.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-3 md:shrink-0">
                  <button
                    type="button"
                    onClick={rejectAll}
                    aria-label="Reject all non-essential cookies"
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold border border-ink/15 text-ink hover:bg-ink hover:text-canvas transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon"
                  >
                    Reject all
                  </button>
                  <button
                    type="button"
                    onClick={openPreferences}
                    aria-label="Customise cookie preferences"
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold text-ink hover:text-salmon underline underline-offset-4 decoration-ink/30 hover:decoration-salmon transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon"
                  >
                    Customise
                  </button>
                  <button
                    type="button"
                    onClick={acceptAll}
                    aria-label="Accept all cookies"
                    className="px-5 py-2.5 rounded-xl text-sm font-bold bg-salmon text-on-salmon hover:bg-salmon transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon focus-visible:ring-offset-2"
                  >
                    Accept all
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <ConsentPreferences />
    </>
  );
}
